import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { ApiError, GetTimetableStructureResponse, WeekdayEnum } from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useNotifications } from '@schooly/components/notifications';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  CrossIcon,
  DeleteIcon,
  Spin,
} from '@schooly/style';
import { TIMETABLE_STRUCTURE } from '@schooly/constants/test-ids';
import { setFormErrorForApiError } from '@schooly/utils/set-form-error-for-api-error';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import { ControlModalHeaderInput } from '../../../../components/uikit-components/Modal/ControlModalHeaderInput';
import {
  ModalContent,
  ModalFooter,
  ModalMain,
  ModalSmall,
} from '../../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../../../components/uikit-components/Modal/ModalHeader';
import { useRouter } from '../../../../context/router/useRouter';
import {
  TimetableStructureContextProps,
  TimetableStructureForm,
  TimetableStructurePeriodForm,
  TimetableStructureTimeSlotForm,
} from '../../../../context/timetableStructure/WithTimetableStructure';
import { useSchool } from '../../../../hooks/useSchool';
import {
  deriveRegistrationGuidance,
  isRegistrationConfirmationRequired,
  resolveSelectedFrequencies,
  seedRegisterSessions,
} from '../registration-helpers';
import { TimetableStructureSettingsStep } from './steps/TimetableStructureSettingsStep';
import { TimetableStructureSlotsStep } from './steps/TimetableStructureSlotsStep';

type ModalStep = 'settings' | 'slots';

const SETTINGS_STEP_FIELDS: (keyof TimetableStructureForm)[] = [
  'name',
  'age_group_ids',
  'weekdays',
  'periods',
];

const defaultValues: TimetableStructureForm = {
  name: '',
  age_group_ids: [],
  weekdays: [
    WeekdayEnum.Monday,
    WeekdayEnum.Tuesday,
    WeekdayEnum.Wednesday,
    WeekdayEnum.Thursday,
    WeekdayEnum.Friday,
  ],
  time_slots: [],
  periods: [],
};

interface SchoolTimetableStructureCreateModalContentProps {
  structure?: GetTimetableStructureResponse;
  isSaving: boolean;
  isDeleting: boolean;
  canDelete: boolean;
  confirmChange: TimetableStructureContextProps['confirmChange'];
  onSave: (data: TimetableStructureForm) => Promise<void>;
  onDelete: () => Promise<void>;
}

export const SchoolTimetableStructureCreateModalContent: FC<
  SchoolTimetableStructureCreateModalContentProps
> = ({ structure, isSaving, isDeleting, canDelete, confirmChange, onSave, onDelete }) => {
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useSchool();
  const { ageGroups } = useAgeGroups({ schoolId, userType: SchoolUserRole.Student });
  const { showNotification } = useNotifications();
  const { goBack } = useRouter();
  const { getConfirmation } = useConfirmationDialog();

  const [step, setStep] = useState<ModalStep>('settings');
  const isLastStep = step === 'slots';

  const isEditing = !!structure;
  const formDefaultValues = structure
    ? {
        name: structure.name,
        age_group_ids: structure.age_group_ids,
        weekdays: structure.weekdays,
        periods: seedRegisterSessions(structure.periods as TimetableStructurePeriodForm[]),
        time_slots: structure.time_slots,
      }
    : defaultValues;

  const form = useForm<TimetableStructureForm>({
    defaultValues: formDefaultValues,
    mode: 'onChange',
  });

  const structureFormErrorOptions = useMemo(
    () => ({
      field: 'name' as const,
      errorToTranslationKey: {
        STRUCTURE_NAME_UNIQUENESS_ERROR: 'school-timetableStructures-UniqueNameError',
      },
    }),
    [],
  );

  const timeSlotsValidation = form.register('time_slots', {
    required: formatMessage({ id: 'school-timetableStructures-Error-NoSlots' }),
  });

  const syncedPeriodIds = useRef<Set<string>>(new Set(formDefaultValues.periods.map((p) => p.id)));
  const syncedWeekdays = useRef<Set<WeekdayEnum>>(new Set(formDefaultValues.weekdays));

  const syncTimeSlots = useCallback(() => {
    const periods = form.getValues('periods');
    const weekdays = form.getValues('weekdays');
    const currentSlots = form.getValues('time_slots');

    const newSlots = periods.flatMap((period) => {
      const isNewPeriod = !syncedPeriodIds.current.has(period.id);

      return weekdays.reduce<TimetableStructureTimeSlotForm[]>((acc, weekday) => {
        const existingSlot = currentSlots.find(
          (slot) => slot.period_id === period.id && slot.weekday === weekday,
        );

        if (existingSlot) {
          acc.push({ ...existingSlot, start_time: period.start_time, end_time: period.end_time });
          return acc;
        }

        const isNewWeekday = !syncedWeekdays.current.has(weekday);

        if (isNewPeriod || isNewWeekday) {
          acc.push({
            period_id: period.id,
            weekday,
            start_time: period.start_time,
            end_time: period.end_time,
          });
        }

        return acc;
      }, []);
    });

    periods.forEach((p) => syncedPeriodIds.current.add(p.id));
    weekdays.forEach((w) => syncedWeekdays.current.add(w));

    form.setValue('time_slots', newSlots, { shouldDirty: true, shouldValidate: true });
  }, [form]);

  const handleContinue = useCallback(async () => {
    const isValid = await form.trigger(SETTINGS_STEP_FIELDS);

    if (isValid) {
      syncTimeSlots();
      setStep('slots');
    }
  }, [form, syncTimeSlots]);

  const handleBack = useCallback(() => {
    setStep('settings');
  }, []);

  const handleClose = useCallback(async () => {
    if (!form.formState.isDirty) {
      goBack();
      return;
    }

    const isConfirmed = await getConfirmation({
      textId: 'school-timetableStructures-CloseConfirmation',
    });

    if (isConfirmed) {
      goBack();
    }
  }, [form.formState.isDirty, getConfirmation, goBack]);

  const handleSubmit = useCallback(
    async (data: TimetableStructureForm) => {
      if (!schoolId) return;
      try {
        await onSave(data);
      } catch (e) {
        setFormErrorForApiError(e as ApiError, form, structureFormErrorOptions, (translationKey) =>
          formatMessage({ id: translationKey }),
        );
      }
    },
    [schoolId, onSave, form, structureFormErrorOptions, formatMessage],
  );

  const handleSaveClick = useCallback(async () => {
    const error = form.formState.errors.time_slots;
    if (error?.message) {
      showNotification({
        message: error.message,
        type: 'error',
        icon: <CrossIcon />,
      });
      return;
    }

    const { age_group_ids, periods } = form.getValues();
    const guidance = deriveRegistrationGuidance(
      resolveSelectedFrequencies(ageGroups, age_group_ids ?? []),
    );

    if (isRegistrationConfirmationRequired(guidance, periods)) {
      const confirmed = await getConfirmation({
        textId: 'school-timetableStructure-registration-mismatch-confirm',
        confirmTextId: 'action-Proceed',
        cancelTextId: 'school-timetableStructure-registration-BackToEditing',
      });
      if (!confirmed) return;
    }

    form.handleSubmit(handleSubmit)();
  }, [form, handleSubmit, showNotification, ageGroups, getConfirmation]);

  const renderContent = () => {
    if (step === 'settings') {
      return <TimetableStructureSettingsStep schoolId={schoolId} confirmChange={confirmChange} />;
    }

    return (
      <TimetableStructureSlotsStep
        timeSlotsValidation={timeSlotsValidation}
        confirmChange={confirmChange}
      />
    );
  };

  const renderFooter = () => {
    if (isLastStep) {
      return (
        <ModalFooter active sx={{ justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowLeftIcon />}
            onClick={handleBack}
            disabled={isSaving}
          >
            <FormattedMessage id="action-Back" />
          </Button>
          <Button
            type="button"
            onClick={handleSaveClick}
            endIcon={isSaving ? <Spin /> : <CheckIcon />}
            disabled={isSaving}
          >
            <FormattedMessage id="action-Save" />
          </Button>
        </ModalFooter>
      );
    }

    return (
      <ModalFooter active sx={{ justifyContent: isEditing ? 'space-between' : 'flex-end' }}>
        {isEditing && (
          <Tooltip
            title={formatMessage({ id: 'school-timetableStructures-DeleteDisabledTooltip' })}
            disableHoverListener={canDelete}
            componentsProps={{ tooltip: { sx: { padding: 1.25 } } }}
          >
            <Box>
              <Button
                variant="outlined"
                startIcon={isDeleting ? <Spin /> : <DeleteIcon />}
                onClick={onDelete}
                disabled={!canDelete || isDeleting || isSaving}
              >
                <FormattedMessage id="action-Delete" />
              </Button>
            </Box>
          </Tooltip>
        )}
        <Button endIcon={<ArrowRightIcon />} onClick={handleContinue} disabled={isDeleting}>
          <FormattedMessage id="action-Continue" />
        </Button>
      </ModalFooter>
    );
  };

  return (
    <ModalSmall open onClose={!isSaving ? handleClose : undefined}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <ModalHeader
            active
            title={
              <ControlModalHeaderInput
                control={form.control}
                name="name"
                rules={{ required: true }}
                placeholder={formatMessage({ id: 'school-timetableStructures-StructureName' })}
                autoFocus={!isEditing}
                inputProps={{ 'data-test-id': TIMETABLE_STRUCTURE.STRUCTURE_NAME_INPUT }}
              />
            }
          >
            {!isSaving && (
              <IconButton onClick={handleClose}>
                <CrossIcon />
              </IconButton>
            )}
          </ModalHeader>

          <ModalMain>
            <ModalContent active>{renderContent()}</ModalContent>
          </ModalMain>

          {renderFooter()}
        </form>
      </FormProvider>
    </ModalSmall>
  );
};
